import { flattenDeep, flow, isNull, isUndefined, uniq, unset } from 'lodash/fp';

import { DatasetDefinition, DatasetFieldType, DatasetSection, DefinitionHierarchy, FormDatasetFields, HiddenField, HiddenFields, HiddenSection, OpenFieldType, SingleDatasetField } from '../../../datasets/store/types';
import { isFormDatasetDefinition, isGroupField } from '../../../datasets/store/typeAssertions';
import { OpenField, OpenFieldSection, OpenSection, SearchField, SearchFieldSection, SearchSection } from '../../../datasets/instances/store/types';
import { ArkDocument } from '../../store/types';
import { DocumentMetaReportField, DocumentReportField } from './types';

// Search Dataset Fields within Dataset Instances
const flattenFormSearchFields = (fields: FormDatasetFields, sections: DatasetSection[], parentFieldId: string, datasetId: string, parentFieldName: string | null, hiddenSectionIds: string[], hiddenSingleFields: HiddenField[], filterDatasetFields: boolean) =>
    flattenDeep(Object.entries(fields)
        // Filter out any sections which are hidden within hiddenFields for datasetId
        .filter(([sectionId]) => !hiddenSectionIds.includes(sectionId))
        .map(([sectionId, section]) => section
            // Filter out datasetFields unless we specify we do not want to when finding parent search fields to establish fields and sections to open
            .filter(({ type }) => !filterDatasetFields || type !== DatasetFieldType.DATASET)
            .map(field => {
                const fieldIsHidden = hiddenSingleFields.find(hiddenField => hiddenField.fieldId === field.id && hiddenField.sectionId === sectionId);
                if (fieldIsHidden) {
                    return [];
                }
                const sectionLabel = parentFieldName || sections.find(({ id }) => id === sectionId)?.label;
                if (isGroupField(field)) {
                    return field.children.map(({ id, label, type }, groupIndex) => ({ fieldId: id!, sectionId, type: OpenFieldType.FIELD, datasetId, parentFieldId, sectionLabel, label, fieldType: type, groupIndex })) as SearchField[];
                }
                return { fieldId: field.id!, sectionId, type: OpenFieldType.FIELD, datasetId, parentFieldId, sectionLabel, label: field.label, fieldType: field.type, groupIndex: undefined };
            })
        )
    ) as SearchField[];

const flattenTableSearchFields = (fields: SingleDatasetField[], parentFieldId: string, datasetId: string, parentFieldName: string | null, hiddenSingleFieldIds: string[], filterDatasetFields: boolean): SearchField[] => fields
    // Filter out datasetFields and any hidden fields for that dataset unless we specify we do not want to when finding parent search fields to establish fields and sections to open
    .filter(({ type, id }) => !filterDatasetFields || (type !== DatasetFieldType.DATASET && !hiddenSingleFieldIds.includes(id!)))
    .map(({ id, label, type }) => ({ fieldId: id!, type: OpenFieldType.FIELD, datasetId, parentFieldId, label, sectionLabel: parentFieldName || undefined, fieldType: type }));

// This function gets a flattened array of search fields and search sections which are very similar to HiddenField and HiddenSections with additional information on for labels and field type
export const getFlattenedSearchFields = (definition: DatasetDefinition, parentFieldId: string, parentFieldName: string | null, hiddenSectionIds: string[], hiddenSingleFields: HiddenField[], filterDatasetFields = true) => {
    if (isFormDatasetDefinition(definition)) {
        return flattenFormSearchFields(definition.datasetFields, definition.datasetSections, parentFieldId, definition.datasetId!.toString(), parentFieldName, hiddenSectionIds, hiddenSingleFields, filterDatasetFields);
    }
    const hiddenFieldIds = hiddenSingleFields.map(({ fieldId }) => fieldId);
    return flattenTableSearchFields(definition.datasetFields, parentFieldId, definition.datasetId!.toString(), parentFieldName, hiddenFieldIds, filterDatasetFields);
};

// This will get the parent field name for a reference title for any datasets etc that are not within a section
// By using the parentFieldId and datasetId we are able to find the label of the parent field that the dataset sits within
const getParentFieldName = (definitionHierarchy: DefinitionHierarchy[], definition: DatasetDefinition, foundParentFieldId: string) => {
    let parentFieldName = null;
    const id = definition.datasetId!;
    const instanceInHierarchy = definitionHierarchy.find(({ definition: { datasetId }, fieldId }) => datasetId! === id && fieldId === foundParentFieldId);
    if (instanceInHierarchy && instanceInHierarchy.parentId) {
        const { parentId, sectionId, fieldId } = instanceInHierarchy;
        const parentInHierarchy = definitionHierarchy.find(({ definition: { datasetId } }) => datasetId === parentId);
        if (parentInHierarchy) {
            const parentDefinition = parentInHierarchy.definition;
            if (isFormDatasetDefinition(parentDefinition)) {
                if (!isNull(sectionId)) {
                    parentFieldName = parentDefinition.datasetFields[sectionId]?.find(({ id }) => id === fieldId)?.label || null;
                }
            } else {
                parentFieldName = parentDefinition.datasetFields.find(({ id }) => id === fieldId)?.label || null;
            }
        }
    }
    return parentFieldName;
};

const getParentId = (definitionHierarchy: DefinitionHierarchy[], id: string, parentFieldId: string) => {
    const instanceInHierarchy = definitionHierarchy.find(({ definition: { datasetId }, fieldId }) => datasetId! === parseInt(id) && parentFieldId === fieldId);
    if (instanceInHierarchy && instanceInHierarchy.parentId) {
        return instanceInHierarchy.parentId;
    }
    return null;
};

export const getAllFlattenedSearchFields = (definitionHierarchy: DefinitionHierarchy[], hiddenFields: HiddenFields) => {
    const hiddenFieldSections = flattenDeep(Object.entries(hiddenFields).map(([datasetId, fields]) => fields.map(fieldSection => ({ ...fieldSection, datasetId }))));
    // Establish which if any of the datasets are completely hidden due to a parent section or field being toggled off in the hidden fields
    // If a dataset is completely hidden then we can return no search fields for this dataset
    const hiddenDatasetIds = uniq(hiddenFieldSections.reduce((acc: string[], cur) => {
        const definition = definitionHierarchy.find(({ definition: { datasetId } }) => datasetId!.toString() === cur.datasetId)?.definition;
        if (definition) {
            // If a hidden field or section has a sectionId then we can assert it is a FormInstance
            if (cur.sectionId) {
                if (cur.type === OpenFieldType.SECTION) {
                    const hiddenDatasetIds = (definition.datasetFields as FormDatasetFields)[cur.sectionId].filter(({ type }) => type === DatasetFieldType.DATASET).map(field => (field as SingleDatasetField).settings.datasetLinked!);
                    return [...acc, ...hiddenDatasetIds];
                }
                if (cur.type === OpenFieldType.FIELD) {
                    const datasetField = (definition.datasetFields as FormDatasetFields)[cur.sectionId].find(({ id }) => id === cur.fieldId);
                    if (datasetField && datasetField.type === DatasetFieldType.DATASET) {
                        return [...acc, (datasetField as SingleDatasetField).settings.datasetLinked!];
                    }
                }
                return acc;
            }
            const hiddenDatasetIds = (definition.datasetFields as SingleDatasetField[]).filter(({ type }) => type === DatasetFieldType.DATASET).map(field => (field as SingleDatasetField).settings.datasetLinked!);
            return [...acc, ...hiddenDatasetIds];
        }
        return acc;
    }, []));

    const searchFields = flattenDeep(definitionHierarchy.map(({ definition, fieldId }) => {
        if ((definition.datasetId && hiddenDatasetIds.includes(definition.datasetId.toString())) || isUndefined(definition.datasetId)) {
            return [];
        }
        const datasetId = definition.datasetId!;
        // For each dataset, we establish what the hidden sections and hidden fields are within to decide whether to include them in the dataset search fields
        const hiddenSectionIds = hiddenFields[datasetId] && (hiddenFields[datasetId].filter(({ type }) => type === OpenFieldType.SECTION) as HiddenSection[]).map(({ sectionId }) => sectionId) || [];
        const hiddenSingleFields = hiddenFields[datasetId] && hiddenFields[datasetId].filter(({ type }) => type === OpenFieldType.FIELD) as HiddenField[] || [];
        const isTableOrSingleSectionDataset = isNull(definition.datasetSections) || isUndefined(definition.datasetSections) || definition.datasetSections.length === 1;
        const searchSections: SearchSection[] = !isTableOrSingleSectionDataset && definition.datasetSections!.filter(({ id }) => !hiddenSectionIds.includes(id)).map(({ id, label }) => ({ sectionId: id, datasetId: datasetId.toString(), type: OpenFieldType.SECTION, parentFieldId: fieldId, sectionLabel: label })) || [];
        const parentFieldName = isTableOrSingleSectionDataset ? getParentFieldName(definitionHierarchy, definition, fieldId) : null;
        const searchFields: SearchField[] = getFlattenedSearchFields(definition, fieldId, parentFieldName, hiddenSectionIds, hiddenSingleFields);
        return [...searchSections, ...searchFields];
    }));
    return searchFields;
};

export const calculateOpenFieldSections = (match: SearchFieldSection, definitionHierarchy: DefinitionHierarchy[]) => {
    // Initially, we check if the match which is selected is within a section which should be opened
    const initialOpenFieldsAndSections: OpenFieldSection[] = match.sectionId ? [{ sectionId: match.sectionId, datasetId: match.datasetId, type: OpenFieldType.SECTION, parentFieldId: match.parentFieldId }] : [];
    let parentId = getParentId(definitionHierarchy, match.datasetId, match.parentFieldId);
    let currentParentFieldId = match.parentFieldId;
    let openFieldsAndSections: OpenFieldSection[] = initialOpenFieldsAndSections;
    // While we are not at the top level parent instance we need to iterate up the tree, going to the next parent and opening any dataset fields and sections which are releavnt to the next parent
    while (!isNull(parentId)) {
        const parentInHierarchy = definitionHierarchy.find(({ definition: { datasetId } }) => datasetId === parentId);
        if (parentInHierarchy) {
            const { definition, fieldId } = parentInHierarchy;
            // Get the parent instance search fields to allow us to easily find the correct fields within that parent and identify where the dataset field is and open it accordingly as an OpenField
            const allParentSearchFields = getFlattenedSearchFields(definition, fieldId, null, [], [], false);
            const parentSearchField = allParentSearchFields.find(({ fieldId }) => fieldId === currentParentFieldId);
            if (parentSearchField && parentSearchField.fieldType === DatasetFieldType.DATASET && parentSearchField.type === OpenFieldType.FIELD) {
                const openField: OpenField = flow(
                    unset('fieldType'),
                    unset('label'),
                    unset('sectionLabel')
                )(parentSearchField);
                openFieldsAndSections.push(openField);
                // Similar to above, if the parentSearchField is within a section, we have to open up that section as well
                if (parentSearchField.sectionId) {
                    const openSection: OpenSection = { sectionId: parentSearchField.sectionId, datasetId: parentSearchField.datasetId, type: OpenFieldType.SECTION, parentFieldId: parentSearchField.parentFieldId };
                    openFieldsAndSections.push(openSection);
                }
                parentId = getParentId(definitionHierarchy, parentSearchField.datasetId, parentSearchField.parentFieldId);
                currentParentFieldId = parentSearchField.parentFieldId;
            }
        } else {
            // If there is no parentInstance then we set the parentId to null which will escape the while loop and return the openFieldsAndSections
            parentId = null;
        }
    }
    return openFieldsAndSections;
};

export const getIsSelectedMetaField = (documentProperty: keyof ArkDocument, selectedMetaFields: DocumentMetaReportField[]) => {
    switch (documentProperty) {
        case 'documentName':
            return selectedMetaFields.includes(DocumentMetaReportField.DOCUMENT_NAME);
        case 'entityAName':
            return selectedMetaFields.includes(DocumentMetaReportField.ENTITY_A);
        case 'entityBName':
            return selectedMetaFields.includes(DocumentMetaReportField.ENTITY_B);
        case 'executedDate':
            return selectedMetaFields.includes(DocumentMetaReportField.EXECUTED_DATE);
        case 'datedAsOf':
            return selectedMetaFields.includes(DocumentMetaReportField.DATED_AS_OF_DATE);
        default:
            return false;
    }
};

const getFieldName = (datasetDefinition: DatasetDefinition, sectionId: string | null) => {
    if (datasetDefinition) {
        if (isFormDatasetDefinition(datasetDefinition)) {
            const sectionLabel = datasetDefinition.datasetSections.find(({ id }) => id === sectionId)?.label;
            return sectionLabel || datasetDefinition.datasetTitle;
        }
        return datasetDefinition.datasetTitle;
    }
    return '';
};

export const getDocumentReportField = (definitionInHierarchy: DefinitionHierarchy, field: SingleDatasetField, sectionId: string | null): DocumentReportField => {
    const { definition, fieldId } = definitionInHierarchy;
    const { datasetId, datasetTitle, datasetType } = definition;
    const { label, id, description } = field;
    const fieldName = getFieldName(definition, sectionId);
    return { datasetId: datasetId!, datasetTitle, datasetType, description, fieldName, id: id!, label, parentFieldId: fieldId, customLabel: '', includeDatasetTitle: fieldName !== label };
};

const flattenFormDefinitionFields = (fields: FormDatasetFields, selectedSectionId: string | null) => flattenDeep(Object.entries(fields).filter(([sectionId]) => isNull(selectedSectionId) || sectionId === selectedSectionId).map(([, fields]) => fields.map(field => isGroupField(field) ? field.children.map(field => field) : field))) as SingleDatasetField[];
export const getFlattenedFields = (definition: DatasetDefinition, sectionId: string | null = null): SingleDatasetField[] => {
    if (isFormDatasetDefinition(definition)) {
        return flattenFormDefinitionFields(definition.datasetFields, sectionId).filter(({ type }) => type !== DatasetFieldType.DATASET);
    }
    return definition.datasetFields.map(field => field).filter(({ type }) => type !== DatasetFieldType.DATASET);
};

